import { Check, ChevronRight, LoaderCircle } from 'lucide-react';
import { useEffect, useState } from 'react';
import { queryClient, queryKeys } from '../../../lib/query-client';
import { goalArc, goalBadge } from '../../../lib/goal-status';
import type { WebGoal, WebGoalTask } from '../../../lib/goals';
import { findGoal, goalMembers, membersDone, useWorkspaceGoals } from '../../../lib/goals';
import type { LedgerEntry, LedgerText, LedgerView, WebLedgerChange, WebLedgerDecision } from '../../../lib/goal-ledger';
import { changeLabelKey, decisionOutcomeCopy, ledgerChangeText } from '../../../lib/goal-ledger';
import { useGoalLedger } from '../../../lib/use-goal-ledger';
import type { WebGoalRound } from '../../../lib/goal-rounds';
import { cadenceLabelKey, knownCadence, resolveRoundsView, roundClosureLabelKey, roundClosureVariant } from '../../../lib/goal-rounds';
import { useTranslation } from '../../../lib/i18n';
import { useMyAgents } from '../../../lib/my-agents';
import { taskStatusBadge } from '../../../lib/task-status';
import { gatingBlocker, isNotStartedTask, isScheduledTask, scheduledDateLabel } from '../../../lib/task-schedule';
import { goalTimelineDays } from '../../../lib/goal-timeline';
import { awaitsDecision, showsLiveMarker } from '../../../lib/agent-task-buckets';
import { useAgentName } from '../../../lib/use-agent-name';
import { useOpenSideChat } from '../../../lib/use-open-side-chat';
import { AgentDot } from '../../AgentDot';
import { Markdown } from '../../Markdown';
import { Spinner } from '../../Spinner';
import { Modal } from '../../Modal';
import { useTaskSidePanel } from '../task-side-panel/TaskSidePanelContext';
import { TaskDetailModal } from '../tasks/TaskDetailModal';
import { DecisionAnswerBlock } from './DecisionAnswerBlock';
import { Badge, Button, ProgressBar, ProgressRing } from '../ui';
import styles from './GoalDetailsModal.module.css';

// The goal-details centered modal (Figma 12815-170575) — the replacement for the docked goal side panel,
// laid out like the native `GoalScreen`: the goal title is the DS modal header; the body carries the brief
// under a "Goal" label, a progress card reading "{percent}% {status}" over a completion bar, then "The
// plan" ("{done} of {total} tasks done" + the goal's tasks, each numbered "{n}/{total}"). The footer
// carries Close and "Talk to {agent}", which opens the side chat over the current page. Same data + strings.
export function GoalDetailsModal({ goal }: { goal: WebGoal }) {
  const { t } = useTranslation();
  const agent = useAgentName();
  const openSideChat = useOpenSideChat();
  const { openModal, closeModal, back: popModal, backLabel } = useTaskSidePanel();
  const teamState = useMyAgents();
  const team = teamState.status === 'ready' ? teamState.team.agents : [];

  // The same on-screen state the card shows, so opening a full ring can't flip the badge back to
  // "In progress" (native applies the 100% override in both places too) — and a goal that SETTLED
  // wears its recorded outcome rather than a bare "Done".
  const badge = goalBadge(goal);
  const title = goal.title?.trim() || t('goals.untitled');
  const total = goal.tasks.length;
  const done = goal.tasks.filter((task) => task.status === 'COMPLETED').length;
  const members = goalMembers(goal);

  const onTalk = () => {
    closeModal();
    openSideChat({ kind: 'new', message: t('goals.chatSeed', { title }) });
  };

  return (
    <Modal
      open
      onClose={closeModal}
      size="small"
      maxHeightPx={650}
      labelledBy="goal-detail-title"
      title={title}
      headerSeparator
      // A MEMBER goal opened from its parent's panel returns to that parent, by the same rule the task
      // drill-in follows — the stack's depth decides, so a goal opened from the grid grows nothing.
      backAction={popModal && backLabel ? { label: backLabel, onBack: popModal } : undefined}
      footerSeparator
      scrollBody
      footer={
        <>
          <Button variant="outlined" onClick={closeModal}>
            {t('common.close')}
          </Button>
          <Button onClick={onTalk}>{t('goals.talk', { agent: agent.short })}</Button>
        </>
      }
    >
      <div className={styles.root}>
        {/* "Goal" label over the brief, per the Figma panel. */}
        {goal.brief.trim() && (
          <div className={styles.briefSection}>
            <span className={styles.label}>{t('goals.detail.title')}</span>
            <div className={styles.brief}>
              <Markdown variant="compact">{goal.brief}</Markdown>
            </div>
          </div>
        )}

        {/* Percent + status read as ONE bold line, over a plain completion fill. */}
        <section className={styles.progressCard}>
          <div className={styles.progressHead}>
            <span className={styles.progressPct}>{goal.percent}%</span>
            <span className={styles.progressStatus}>{t(badge.labelKey)}</span>
          </div>
          <ProgressBar progress="success" value={goal.percent} decorative />
        </section>

        {/* The goal's MEMBERS, above "The plan". A goal made of goals usually has no tasks of its own —
            its work is on its members — so an empty plan is not the whole story, and reading the members
            first is the only way that goal makes sense. Each member is itself a goal: its own state
            (its outcome once it settles), its own ring, and a tap that opens IT in this same panel. */}
        {members.length > 0 && (
          <section className={styles.section}>
            <span className={styles.label}>{t('goals.members.title')}</span>
            <span className={styles.count}>{t('goals.members.count', { done: membersDone(members), total: members.length })}</span>
            <div className={styles.members}>
              {members.map((member) => (
                <GoalMemberRow
                  key={member.id}
                  member={member}
                  onOpen={() =>
                    openModal(<LiveGoalDetailsModal workspaceId={goal.workspaceId} goalId={member.id} initialGoal={member} />, member.id, {
                      backLabel: t('common.backTo', { title }),
                    })
                  }
                />
              ))}
            </div>
          </section>
        )}

        {/* The ROUNDS of a standing member — which turn it is in, when that turn closes, and how the
            turns before it ended. Renders NOTHING at all for a member with no cadence: an ordinary
            one-off member has no turns, and an empty "Turns" section would read as a rhythm it failed
            to keep. Above the plan, because a standing member's plan is the work of the current turn. */}
        <GoalRoundsSection goal={goal} />

        {/* The TIMELINE — the plan by date. Renders NOTHING when no task carries a date, directly or through
            the blocker it waits for: an empty timeline would read as a plan with no dates, which every plan
            without one already is. Above the plan, because when a plan HAS dates the reader's first question
            is "when", and the plan answers "what". */}
        <GoalTimelineSection goal={goal} onOpenTask={(task) => openModal(<TaskDetailModal taskId={task.id} />, task.id, { backLabel: t('common.backTo', { title }) })} />

        <section className={styles.section}>
          <span className={styles.label}>{t('goals.plan.title')}</span>
          <span className={styles.count}>{t('goals.plan.count', { done, total })}</span>
          {total === 0 ? (
            <div className={styles.empty}>{t('goals.plan.empty')}</div>
          ) : (
            <div className={styles.plan}>
              {goal.tasks.map((task, i) => (
                <GoalTaskBlock
                  key={task.id}
                  index={i}
                  total={total}
                  task={task}
                  agentName={agentNameOf(task, team)}
                  onOpen={() => openModal(<TaskDetailModal taskId={task.id} />, task.id, { backLabel: t('common.backTo', { title }) })}
                />
              ))}
            </div>
          )}
        </section>

        {/* The LEDGER — what actually happened to this goal: the questions it put to you, how each one
            ended, and the plan changes that landed with them. Last, because it is history: the reader
            comes to a goal for where it stands, and stays for how it got there. */}
        <GoalLedgerSection goal={goal} />
      </div>
    </Modal>
  );
}

/**
 * Keeps the modal's goal fresh across duplexer-driven refetches: the modal slot stores the React node
 * created at click time, so a concrete `goal` would freeze the plan until reopened. This wrapper keys by
 * goal id and re-selects the latest query result.
 */
export function LiveGoalDetailsModal({
  workspaceId,
  goalId,
  initialGoal,
}: {
  workspaceId: string | undefined;
  goalId: string;
  initialGoal: WebGoal;
}) {
  const state = useWorkspaceGoals(workspaceId);
  // Searches members too — a member opened from its parent's panel is not in the top level of the list,
  // and looking only there would freeze it on the copy captured at click time.
  const liveGoal = state.status === 'ready' ? findGoal(state.goals, goalId) : undefined;
  return <GoalDetailsModal goal={liveGoal ?? initialGoal} />;
}

/**
 * The goal behind a PLAN question, opened from a card that holds only the question (`planReviewGoalId`): the island,
 * the Action needed tab, a step's own page. Those carry a goal id and no goal, so this reads the account-wide
 * goals list (the cache the Goals screen and the island already warm) and renders the live goal. Until the list
 * is in, the same modal shell waits; a goal the list does not hold (closed meanwhile, or a failed read) says so
 * rather than drawing an empty plan.
 */
export function GoalByIdModal({ goalId }: { goalId: string }) {
  const { t } = useTranslation();
  const { closeModal } = useTaskSidePanel();
  const state = useWorkspaceGoals();
  const goal = state.status === 'ready' ? findGoal(state.goals, goalId) : undefined;
  // A cached list can predate the plan (laid out a moment ago), so a miss re-reads once before it is called a miss.
  const [reread, setReread] = useState<'idle' | 'reading' | 'done'>('idle');
  const missing = state.status === 'ready' && !goal;
  useEffect(() => {
    if (!missing || reread !== 'idle') return;
    setReread('reading');
    void queryClient.refetchQueries({ queryKey: queryKeys.workspaceGoals(undefined) }).finally(() => setReread('done'));
  }, [missing, reread]);
  if (goal) return <GoalDetailsModal goal={goal} />;
  const waiting = state.status === 'loading' || (missing && reread !== 'done');
  return (
    <Modal open onClose={closeModal} size="small" maxHeightPx={650} scrollBody title={t('chat.goalCard.seeFullPlan')} headerSeparator>
      <div className={styles.root} data-testid="goalByIdPending">
        {waiting ? (
          <Spinner size={20} />
        ) : (
          <div className={styles.empty}>{t('goals.error')}</div>
        )}
      </div>
    </Modal>
  );
}

/**
 * One member in the goal's "Members" section: its own completion ring, its own state badge — its
 * recorded OUTCOME once it has settled, so a member the user called off never wears the green Done chip —
 * its title, and a "Required" mark when the parent may not achieve without it. The row is a button: a
 * member IS a goal, so tapping it opens that goal in this same panel.
 */
function GoalMemberRow({ member, onOpen }: { member: WebGoal; onOpen: () => void }) {
  const { t } = useTranslation();
  const badge = goalBadge(member);
  // A standing member is marked in the LIST too, so the reader can tell which rows recur without opening
  // each one. It is the DS's one outlined Style — a category, beside the state badge rather than a second
  // competing state. A member with no cadence gets no mark, which is the honest absence.
  const cadence = knownCadence(member.cadence);
  return (
    <button type="button" className={styles.memberRow} onClick={onOpen}>
      <ProgressRing percent={member.percent} arcColor={goalArc(member)} size={40} stroke={6} />
      <span className={styles.memberBody}>
        <span className={styles.memberHead}>
          <Badge variant={badge.variant} label={t(badge.labelKey)} />
          {cadence && <Badge variant="agent-profession" label={t(cadenceLabelKey(cadence))} />}
          {member.requiredForParent && <span className={styles.memberRequired}>{t('goals.members.required')}</span>}
        </span>
        <span className={styles.memberTitle}>{member.title?.trim() || t('goals.untitled')}</span>
      </span>
      <ChevronRight aria-hidden size={18} className={`${styles.memberChevron} dir-flip`} />
    </button>
  );
}

/** A turn's date, in the reader's own locale. Empty when the instant is missing or unparseable — a
 *  turn with no readable deadline says nothing about one rather than rendering "Invalid Date". */
function formatRoundWhen(value: string | undefined): string {
  if (!value) return '';
  const at = new Date(value);
  return Number.isNaN(at.getTime()) ? '' : at.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

/**
 * The goal's turns, when it is a STANDING member — the client half of "the round is the card".
 *
 * Every decision here is `lib/goal-rounds.ts`'s; this only draws it. The four states it can be in are
 * deliberately different things and none of them is a count: a member with no cadence renders nothing at
 * all, a standing member whose rounds we could not read says so rather than showing an empty list, and
 * one that simply has no turns yet says that in words. Rounds are new — most standing members have none —
 * so "0 turns" would be a number nobody measured.
 */
function GoalRoundsSection({ goal }: { goal: WebGoal }) {
  const { t } = useTranslation();
  const cadence = knownCadence(goal.cadence);
  const view = resolveRoundsView(goal, Date.now());
  if (!cadence || view.kind === 'absent') return null;
  return (
    <section className={styles.section}>
      <span className={styles.label}>{t('goals.rounds.title')}</span>
      <span className={styles.count}>{t(cadenceLabelKey(cadence))}</span>
      {view.kind === 'unavailable' && <div className={styles.empty}>{t('goals.rounds.unavailable')}</div>}
      {view.kind === 'awaiting' && <div className={styles.empty}>{t('goals.rounds.awaiting')}</div>}
      {view.kind === 'rounds' && (
        <div className={styles.rounds}>
          {view.current && <GoalRoundRow round={view.current} running />}
          {view.past.map((round) => (
            <GoalRoundRow key={round.id} round={round} />
          ))}
        </div>
      )}
    </section>
  );
}

/**
 * One turn: its state badge and the instant that state is about.
 *
 * A running turn is dated by its DEADLINE ("Closes …") — that is the thing the reader is waiting on. A
 * finished one is dated by when it actually closed. `CLOSED_LATE_AND_SHORT` takes the neutral grey, never
 * the green tick: the cadence got stuck and the deadline stopper closed the turn, which is an ending, not
 * a success. The badge Style comes from `roundClosureVariant`; nothing here hands out a colour.
 */
function GoalRoundRow({ round, running = false }: { round: WebGoalRound; running?: boolean }) {
  const { t } = useTranslation();
  const when = formatRoundWhen(running ? round.closesAt : round.closedDate ?? round.closesAt);
  return (
    <div className={styles.roundRow}>
      {running ? (
        <Badge variant="in-progress" label={t('goals.rounds.current')} />
      ) : (
        <Badge variant={roundClosureVariant(round.closure)} label={t(roundClosureLabelKey(round.closure))} />
      )}
      {when && <span className={styles.roundWhen}>{t(running ? 'goals.rounds.closes' : 'goals.rounds.closedOn', { when })}</span>}
    </div>
  );
}

function agentNameOf(task: WebGoalTask, team: { id: string; name?: string }[]): string {
  if (!task.agentId) return '';
  return team.find((member) => member.id === task.agentId)?.name?.trim() || '';
}

// One task in the plan: its "{n}/{total}" counter, title + true status badge, the agent working it, and its
// step checklist. Mirrors native's `GoalTaskBlock`.
//
// The block is a BUTTON, and it opens the ordinary task drill-in (`TaskDetailModal`) for that task. The
// plan used to be a read-only recital: the user could see that "Choose the book" existed and was waiting,
// and could do nothing about it from here — no transcript, no edit, no note, no cancel — while the very
// same task one screen over on Tasks offered all four. Reported as "the details screen is not actionable".
// So this deliberately does NOT grow its own action set: the task detail already owns the conversation,
// the edit, the notes and the cancel flow, and a second implementation of them here would be a second
// place for them to drift. Same idiom as the member row above, which opens a member's own goal panel.
/** The day label: "Today" for today, else the short weekday inside the coming week, else a short date —
 *  the same rule the chips follow (`scheduledDateLabel`), so a row and its day never name the date two ways. */
function timelineDayLabel(dayMs: number, now: number, locale: string | undefined, t: (k: string) => string): string {
  const today = new Date(now);
  today.setHours(0, 0, 0, 0);
  if (dayMs === today.getTime()) return t('goals.timeline.today');
  // A PAST day is always a short date, never a weekday: the chip rule's "inside the coming week" test is
  // signed, so a day three weeks ago would otherwise read "Mon". Shared 1:1 with iOS `ScheduledDateLabel.dayText`.
  if (dayMs < today.getTime()) return new Date(dayMs).toLocaleDateString(locale, { day: 'numeric', month: 'short' });
  return scheduledDateLabel(new Date(dayMs), now, locale);
}

function GoalTimelineSection({ goal, onOpenTask }: { goal: WebGoal; onOpenTask: (task: WebGoalTask) => void }) {
  const { t, i18n } = useTranslation();
  const now = Date.now();
  const days = goalTimelineDays(goal.tasks, now);
  if (days.length === 0) return null;
  const today = new Date(now);
  today.setHours(0, 0, 0, 0);
  const todayMs = today.getTime();
  return (
    <section className={styles.section} data-testid="goalTimeline">
      <span className={styles.label}>{t('goals.timeline.title')}</span>
      <ol className={styles.timeline}>
        {days.map((day, i) => (
          <li key={day.dayMs} className={`${styles.timelineDay} ${day.dayMs < todayMs ? styles.timelineDayPast : ''}`} data-testid={`goalTimelineDay-${i}`}>
            <span className={styles.timelineWhen}>{timelineDayLabel(day.dayMs, now, i18n.language || undefined, t)}</span>
            <span aria-hidden className={`${styles.timelineDot} ${day.dayMs === todayMs ? styles.timelineDotToday : ''}`} />
            <div className={styles.timelineTasks}>
              {day.dated.map((task, j) => (
                <button type="button" key={task.id} className={styles.timelineTask} onClick={() => onOpenTask(task)} data-testid={`goalTimelineTask-${i}-${j}`}>
                  <span aria-hidden className={styles.stepMark}>{task.status === 'COMPLETED' && <Check size={14} />}</span>
                  <span className={`${styles.timelineTaskTitle} ${task.status === 'COMPLETED' ? styles.stepTextDone : ''}`}>{task.title?.trim() || t('tasks.untitledTask')}</span>
                </button>
              ))}
              {day.gated.map(({ task }, j) => (
                <button type="button" key={task.id} className={`${styles.timelineTask} ${styles.timelineTaskGated}`} onClick={() => onOpenTask(task)} data-testid={`goalTimelineTask-${i}-${day.dated.length + j}`}>
                  <span aria-hidden className={styles.timelineHang}>↳</span>
                  <span className={`${styles.timelineTaskTitle} ${task.status === 'COMPLETED' ? styles.stepTextDone : ''}`}>{task.title?.trim() || t('tasks.untitledTask')}</span>
                </button>
              ))}
            </div>
          </li>
        ))}
      </ol>
    </section>
  );
}

function GoalTaskBlock({
  index,
  total,
  task,
  agentName,
  onOpen,
}: {
  index: number;
  total: number;
  task: WebGoalTask;
  agentName: string;
  onOpen: () => void;
}) {
  const { t } = useTranslation();
  // A task the plan LAID OUT and nobody has started yet, by the shared rule (`lib/task-schedule.ts`) that
  // the Tasks screen, the island and the task detail all ask. The plan used to answer this question twice
  // by itself and get it wrong both times: the status chip read "In progress" because PENDING files into
  // the in-progress bucket, and the step list span a "current step" glyph because it picked the first
  // incomplete step with no notion of whether any agent had begun. Observed in production on the book-club
  // goal — 0%, "0 of 2 tasks done", and both tasks looking like work in flight.
  //
  // Same precedence as the Tasks list card: scheduled (a date chip) > gated ("After <blocker>") > not
  // started > the bucket badge. This row used to ask only the not-started half, so a task held for Sep 28
  // wore "In progress" here while the Tasks list filed the very same row under LATER with its date —
  // Ronny's screenshots of one goal on two screens. The Timeline above the plan is drawn from the same
  // two facts (`effectiveStartMs`), so the chip and the timeline cannot disagree either.
  const { i18n } = useTranslation();
  const now = Date.now();
  const scheduled = isScheduledTask(task, now);
  const gating = gatingBlocker(task, now);
  const notStarted = isNotStartedTask(task, now);
  // Same reason as the in-chat goal card's badge: a blocked task is PENDING on the wire, so without this
  // flag the plan badges it "In progress" while the Tasks screen badges the same task "Action needed".
  const badge = taskStatusBadge(task.status, { awaitingDecision: awaitsDecision(task), notStarted });
  const chip = scheduled ? (
    <Badge variant="waiting-on-a-reply" label={scheduledDateLabel(task.postAt ?? task.notBefore, now, i18n.language || undefined)} className={styles.taskBadge} />
  ) : gating ? (
    <Badge variant="waiting-on-a-reply" label={t('tasks.status.after', { title: gating.title })} className={`${styles.taskBadge} ${styles.taskBadgeAfter}`} />
  ) : (
    <Badge variant={badge.variant} label={t(badge.labelKey)} className={styles.taskBadge} />
  );
  return (
    <button type="button" className={styles.taskBlock} onClick={onOpen}>
      <span className={styles.taskIndex}>{`${index + 1}/${total}`}</span>
      <div className={styles.taskHead}>
        <span className={styles.taskTitle}>{task.title?.trim() || t('tasks.untitledTask')}</span>
        {chip}
        <ChevronRight aria-hidden size={16} className={`${styles.taskChevron} dir-flip`} />
      </div>
      {agentName && (
        <div className={styles.agent}>
          <AgentDot id={task.agentId || agentName} size={10} />
          <span className={styles.agentName}>{agentName}</span>
        </div>
      )}
      {task.steps.length > 0 &&
        (() => {
          // The step ahead of the first incomplete one is "current" (spinner glyph); everything after it is
          // "future" (no glyph, muted) — per the Figma panel.
          //
          // The spinner means "this is happening now", so it is gated on the shared MARKER rule
          // (`showsLiveMarker`) rather than on the not-started rule alone. Not-started was only ONE of the
          // ways a step could spin over nothing: a task handed back to you (ACTION_NEEDED) or paused has no
          // loop running either, and both were drawing a spinner on whichever step came next. Observed in
          // production — a step reading "Send next follow-up Sep 4 if no reply" spinning on Sep 9, on a task
          // the watchdog had stranded four days earlier.
          //
          // ⚠️ This used to call `isInMotion`, and the comment here claimed it "falls back to the bucket,
          // where `bucketOfTask` files ACTION_NEEDED / PAUSED under `waiting`". That fallback was REMOVED
          // afterwards (deliberately — see the note in `isInMotion`), so the guarantee this site was
          // relying on had quietly stopped holding and the Sep-9 bug was reachable again. `showsLiveMarker`
          // is that guarantee stated as its own rule instead of borrowed from another one: a marker is for
          // a row the reader is not being asked about, which is exactly `bucket !== 'waiting'`.
          //
          // -1 leaves every step in its plain state rather than promoting the next one to "future", which is
          // right — nothing here is after the current step, because there is no current step.
          const currentIndex = showsLiveMarker(task) ? task.steps.findIndex((step) => !step.completed) : -1;
          return (
            <div className={styles.steps}>
              {task.steps.map((step, i) => {
                const isCurrent = i === currentIndex;
                const isFuture = currentIndex !== -1 && i > currentIndex;
                return (
                  <div key={i} className={styles.step}>
                    <span aria-hidden className={styles.stepMark}>
                      {step.completed && <Check size={14} />}
                      {isCurrent && <LoaderCircle size={14} />}
                    </span>
                    <span
                      className={`${styles.stepText} ${step.completed ? styles.stepTextDone : ''} ${isFuture ? styles.stepTextFuture : ''}`}
                    >
                      {step.title}
                    </span>
                  </div>
                );
              })}
            </div>
          );
        })()}
    </button>
  );
}

/** An entry's instant, in the reader's own locale. Empty when it is missing or unparseable — an entry
 *  with no readable date says nothing about one rather than rendering "Invalid Date". Same rule, and the
 *  same format, as `formatRoundWhen` above. */
function formatLedgerWhen(value: string | undefined): string {
  return formatRoundWhen(value);
}

/**
 * The goal's LEDGER — the decisions it put to you and the changes its plan recorded, as one story.
 *
 * Every rule it draws is `lib/goal-ledger.ts`'s and mirrored on both phones. What matters here:
 *
 *  - **"Waiting on you" is not history.** Open questions get their own heading ABOVE the log, because a
 *    question still being asked has not happened yet and filing it under "what happened" both misleads
 *    the reader and strands them in a list they cannot act on.
 *  - **A decision and the plan change beside it are ONE entry.** The fold happens in `buildGoalLedger`;
 *    this renders the consequence as part of the decision rather than as a second row saying the same
 *    event again.
 *  - **Empty is a sentence**, never a header over nothing — and a failed read says so instead of
 *    claiming the goal has no history.
 *  - **A cut is announced.** `truncated` puts a line under the log; a silent slice reads as "that is all
 *    there is", which is the one thing a log must never imply.
 */
function GoalLedgerSection({ goal }: { goal: WebGoal }) {
  const { t } = useTranslation();
  const view = useGoalLedger(goal.id, goal.parentGoalId);
  // Nothing at all while the first read is in flight: an empty state that resolves into a full history a
  // moment later is worse than a beat of nothing.
  if (!view) return null;
  return (
    <>
      {view.kind === 'ledger' && view.open.length > 0 && (
        <section className={styles.section}>
          <span className={styles.label}>{t('goals.ledger.open.title')}</span>
          <div className={styles.ledgerOpen}>
            {view.open.map((decision, index) => (
              <GoalLedgerOpenRow key={decision.id} decision={decision} goal={goal} index={index} />
            ))}
          </div>
        </section>
      )}
      <section className={styles.section}>
        <span className={styles.label}>{t('goals.ledger.title')}</span>
        {view.kind === 'unavailable' ? (
          <div className={styles.empty}>{t('goals.ledger.unavailable')}</div>
        ) : view.entries.length === 0 ? (
          <div className={styles.empty}>{t('goals.ledger.empty')}</div>
        ) : (
          <>
            <div className={styles.ledger}>
              {view.entries.map((entry) => (
                <GoalLedgerRow key={entry.kind === 'decision' ? entry.decision.id : entry.change.id} entry={entry} />
              ))}
            </div>
            {view.truncated && <div className={styles.ledgerCut}>{t('goals.ledger.truncated', { count: view.entries.length })}</div>}
          </>
        )}
      </section>
    </>
  );
}

/** One question still waiting on somebody: what was asked, by when it is needed, and — since the answer
 *  is the only reason a reader is here — the controls to ANSWER it. Dated by its DEADLINE when it has
 *  one, which is what the reader is being hurried on, else by when it was asked.
 *
 *  A blank question falls back to "Waiting on you" rather than rendering nothing, because the row now
 *  carries buttons and an unlabelled Approve/Decline pair is a question nobody can answer.
 *
 *  The row itself stays NON-tappable: only its controls take input. The realistic mis-answer here is a
 *  mis-tap, and a tappable card around an irreversible act is the way to make one. */
function GoalLedgerOpenRow({ decision, goal, index }: { decision: WebLedgerDecision; goal: WebGoal; index: number }) {
  const { t } = useTranslation();
  const when = formatLedgerWhen(decision.dueBy ?? decision.raisedDate);
  const questionId = `goalLedgerOpen-${index}-question`;
  return (
    <div className={styles.ledgerOpenRow} data-testid={`goalLedgerOpen-${index}`}>
      <span id={questionId} className={styles.ledgerQuestion} dir="auto">
        {decision.question?.trim() || t('goals.ledger.open.title')}
      </span>
      {when && <span className={styles.ledgerWhen}>{t(decision.dueBy ? 'goals.ledger.open.due' : 'goals.ledger.asked', { when })}</span>}
      <DecisionAnswerBlock
        decision={decision}
        goalId={goal.id}
        parentGoalId={goal.parentGoalId}
        questionId={questionId}
        index={index}
      />
    </div>
  );
}

/** One thing that happened: a settled question with whatever the plan did alongside it, or a plan change
 *  no question explains. The question and the agent's own change text are LLM-authored, so they carry
 *  `dir="auto"`; our own localized sentences follow the page locale and are left alone. */
function GoalLedgerRow({ entry }: { entry: LedgerEntry }) {
  const { t } = useTranslation();
  if (entry.kind === 'change') {
    return (
      <div className={styles.ledgerRow}>
        <GoalLedgerChangeBody change={entry.change} />
        {formatLedgerWhen(entry.change.date) && <span className={styles.ledgerWhen}>{formatLedgerWhen(entry.change.date)}</span>}
      </div>
    );
  }
  const outcome = decisionOutcomeCopy(entry.decision);
  const when = formatLedgerWhen(entry.decision.settledDate ?? entry.decision.outcome?.at);
  return (
    <div className={styles.ledgerRow}>
      {entry.decision.question?.trim() && (
        <span className={styles.ledgerQuestion} dir="auto">
          {entry.decision.question.trim()}
        </span>
      )}
      <span className={styles.ledgerOutcome}>{t(outcome.key, outcome.params)}</span>
      {entry.changes.length > 0 && (
        <>
          <span className={styles.ledgerMoved}>{t('goals.ledger.planMoved')}</span>
          <div className={styles.ledgerChanges}>
            {entry.changes.map((change) => (
              <GoalLedgerChangeBody key={change.id} change={change} nested />
            ))}
          </div>
        </>
      )}
      {when && <span className={styles.ledgerWhen}>{when}</span>}
    </div>
  );
}

/** A change, in words: which task, what happened to it, and the agent's own note about it.
 *
 * The label goes through `changeLabelKey`, which returns NOTHING for a vocabulary this build does not
 * know — so an unrecognised label renders as the content alone rather than putting
 * `review:changes-requested` on a user's screen. That is the guard, not a fallback. */
function GoalLedgerChangeBody({ change, nested = false }: { change: WebLedgerChange; nested?: boolean }) {
  const { t } = useTranslation();
  const labelKey = changeLabelKey(change.label);
  const title = change.taskTitle?.trim() || t('tasks.untitledTask');
  // NOT `change.content`: `ledgerChangeText` is where a reviewer's transcript is refused outright and
  // where the agent's own markdown becomes one capped plain sentence. Rendering the raw field here is
  // exactly the bug — `**Brief requirements**`, asterisks and all, 3,693 characters of it.
  const text = ledgerChangeText(change);
  return (
    <div className={nested ? styles.ledgerChange : styles.ledgerChangeTop}>
      <span className={styles.ledgerChangeHead}>
        <span className={styles.ledgerTask}>{title}</span>
        {labelKey && <span className={styles.ledgerChangeLabel}>{t(labelKey)}</span>}
      </span>
      {text && <GoalLedgerChangeText text={text} />}
    </div>
  );
}

/**
 * One change's words — the capped sentence, and the rest on request.
 *
 * The toggle only exists when there IS more, and it is the whole announcement that the row was cut: an
 * ellipsis alone reads as the agent's own trailing off, while a control saying "Expand" cannot. It reuses
 * `common.expand` / `common.collapse`, which are already translated in all 37 locales on all three
 * surfaces — a ledger row is not worth 222 new strings of translator debt.
 *
 * Expanding shows `full`, which is still PLAIN: a row that swapped prose for markdown on the way open
 * would put the asterisks back.
 */
function GoalLedgerChangeText({ text }: { text: LedgerText }) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  return (
    <>
      <span className={styles.ledgerContent} dir="auto">
        {expanded ? text.full : text.text}
      </span>
      {text.truncated && (
        <button type="button" className={styles.ledgerMore} onClick={() => setExpanded((value) => !value)}>
          {t(expanded ? 'common.collapse' : 'common.expand')}
        </button>
      )}
    </>
  );
}
